import { useEffect, useMemo, useState } from "react"
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  Search,
  Trash2,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { formatFullDate } from "@/lib/format"
import {
  useDeleteVideoReport,
  useVideoReportsList,
  type VideoReport,
} from "@/lib/queries/videoreports"
import { ExportButton } from "@/components/ExportButton"
import { api } from "@/lib/api"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Reports (moderation queue)
// API:    POST /admin/videoreport_list   (server pagination/sort/search)
//         POST /admin/videoreport_delete { ids: [id] }
//
// LIST request body:
//   page, limit, search?, sortyBy?, sortDirection?    (sortyBy typo PRESERVED)
//
// LIST columns rendered:
//   reporter_name + reporter_email → "Reporter"   (joined from users)
//   video_author + video_narrated_by → "Video"    (joined from videos)
//   created_at  → "Reported"   (sortable, default DESC)
//   View action → opens modal with the full report description + thumb
//   Delete action → POST /admin/videoreport_delete { ids: [_id] }
//
// Replaces the stub Reports.tsx page that shipped in admin-v2 Batch I; the
// backend videoreport_{list,delete} routes were added in the same commit
// as this file.
// ───────────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

function useDebounced<T>(value: T, delay = 300): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay)
    return () => clearTimeout(t)
  }, [value, delay])
  return v
}

export default function Reports() {
  const [search, setSearch] = useState("")
  const debouncedSearch = useDebounced(search, 300)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(10)
  const [sortyBy, setSortyBy] = useState<string | undefined>(undefined)
  const [sortDirection, setSortDirection] = useState<1 | -1>(-1)

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, limit, sortyBy, sortDirection])

  const params = useMemo(
    () => ({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortyBy,
      sortDirection,
    }),
    [page, limit, debouncedSearch, sortyBy, sortDirection]
  )

  const reportsQ = useVideoReportsList(params)
  const deleteMut = useDeleteVideoReport()

  const [viewReport, setViewReport] = useState<VideoReport | null>(null)
  const [deleteReport, setDeleteReport] = useState<VideoReport | null>(null)

  const onSortClick = (field: string) => {
    if (sortyBy === field) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1))
    } else {
      setSortyBy(field)
      setSortDirection(-1)
    }
  }

  const SortIcon = ({ field }: { field: string }) => {
    if (sortyBy !== field) return null
    return sortDirection === 1 ? (
      <ArrowUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 inline h-3 w-3" />
    )
  }

  const totalPages = reportsQ.data?.totalPages ?? 0
  const totalDocs = reportsQ.data?.totalDocs ?? 0

  const handleDeleteConfirm = async () => {
    if (!deleteReport) return
    await deleteMut.mutateAsync(deleteReport._id)
    setDeleteReport(null)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="gap-4 sm:flex-row sm:items-end sm:justify-between sm:space-y-0">
          <div>
            <CardTitle>Reports</CardTitle>
            <CardDescription>
              {totalDocs > 0
                ? `${totalDocs.toLocaleString()} report${totalDocs === 1 ? "" : "s"}`
                : "User-submitted video reports — moderation queue"}
            </CardDescription>
          </div>
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search reports…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                aria-label="Search reports"
              />
            </div>
            <ExportButton<VideoReport>
              filenamePrefix="video-reports"
              totalEstimate={totalDocs}
              columns={[
                { label: "Reporter Name", get: (r) => r.reporter_name },
                { label: "Reporter Email", get: (r) => r.reporter_email },
                { label: "Video Author", get: (r) => r.video_author },
                { label: "Video Narrated By", get: (r) => r.video_narrated_by },
                { label: "Video Proverb", get: (r) => r.video_proverb },
                { label: "Report Description", get: (r) => r.report_desc },
                { label: "Reported At", get: (r) => r.created_at },
                { label: "Report ID", get: (r) => r._id },
                { label: "Video ID", get: (r) => r.video_id },
                { label: "User ID", get: (r) => r.user_id },
              ]}
              request={async (page, limit) => {
                const res = await api.post("/admin/videoreport_list", {
                  page,
                  limit,
                  search: debouncedSearch || undefined,
                  sortyBy,
                  sortDirection,
                })
                return {
                  docs: res.data?.data?.docs ?? [],
                  totalDocs: res.data?.data?.totalDocs ?? 0,
                }
              }}
            />
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {reportsQ.isError && (
            <div
              role="alert"
              className="m-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load reports. Try refreshing.</span>
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Reporter</TableHead>
                <TableHead>Video</TableHead>
                <TableHead className="max-w-[24rem]">Reason</TableHead>
                <SortableTH
                  field="created_at"
                  label="Reported"
                  {...{ onSortClick, SortIcon }}
                />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reportsQ.isLoading && (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                  </TableCell>
                </TableRow>
              )}
              {!reportsQ.isLoading && (reportsQ.data?.docs.length ?? 0) === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center text-muted-foreground">
                    No reports match your filters.
                  </TableCell>
                </TableRow>
              )}
              {reportsQ.data?.docs.map((r) => (
                <TableRow key={r._id}>
                  <TableCell className="whitespace-nowrap">
                    <div className="font-medium">{r.reporter_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.reporter_email || "—"}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="font-medium">
                      {r.video_narrated_by || r.video_author || "—"}
                    </div>
                    <div className="max-w-[18rem] truncate text-xs text-muted-foreground">
                      {r.video_proverb || ""}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[24rem] truncate text-muted-foreground">
                    {r.report_desc || "—"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {formatFullDate(r.created_at)}
                  </TableCell>
                  <TableCell className="space-x-1 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewReport(r)}
                      aria-label="View report"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteReport(r)}
                      aria-label="Delete report"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>

        <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              aria-label="Rows per page"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-3">
            <span>
              Page {page}
              {totalPages > 0 ? ` of ${totalPages}` : ""}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                disabled={totalPages > 0 && page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* View modal — full report description, video thumbnail */}
      <Dialog
        open={!!viewReport}
        onOpenChange={(open) => !open && setViewReport(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Report details</DialogTitle>
            <DialogDescription>
              Reported {formatFullDate(viewReport?.created_at)}
            </DialogDescription>
          </DialogHeader>
          {viewReport && (
            <div className="space-y-4 text-sm">
              <Field
                label="Reporter"
                value={`${viewReport.reporter_name ?? "—"} <${viewReport.reporter_email ?? "—"}>`}
              />
              <Field
                label="Video"
                value={
                  viewReport.video_narrated_by ||
                  viewReport.video_author ||
                  "—"
                }
              />
              {viewReport.video_proverb && (
                <Field label="Proverb" value={viewReport.video_proverb} />
              )}
              {viewReport.video_thumb_url && (
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Thumbnail
                  </div>
                  <img
                    src={viewReport.video_thumb_url}
                    alt=""
                    loading="lazy"
                    className="mt-1 h-auto w-48 rounded border object-cover"
                  />
                </div>
              )}
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Reason
                </div>
                <div className="mt-1 whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-sm">
                  {viewReport.report_desc || "—"}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewReport(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete modal */}
      <Dialog
        open={!!deleteReport}
        onOpenChange={(open) => !open && setDeleteReport(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dismiss report?</DialogTitle>
            <DialogDescription>
              The report from{" "}
              {deleteReport?.reporter_name || deleteReport?.reporter_email || "this user"}{" "}
              will be removed from the queue. The video itself is not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteReport(null)}
              disabled={deleteMut.isPending}
            >
              Keep report
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Dismiss report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 break-words">{value}</div>
    </div>
  )
}

function SortableTH({
  field,
  label,
  className,
  onSortClick,
  SortIcon,
}: {
  field: string
  label: React.ReactNode
  className?: string
  onSortClick: (field: string) => void
  SortIcon: React.ComponentType<{ field: string }>
}) {
  return (
    <TableHead className={cn("cursor-pointer select-none", className)}>
      <button
        type="button"
        onClick={() => onSortClick(field)}
        className="flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground"
      >
        {label}
        <SortIcon field={field} />
      </button>
    </TableHead>
  )
}
